import { View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components/src/lib/pm-components";
import { APP_RECORD_TYPES, Configuration } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import {
  getAllReportingItems,
  withTotalInTitle,
} from "../../../helpers/pages-helpers/dashboard-plugin-helpers";
import { filterReportingItemsByRecordType } from "../../../utils/filter-reporting-items-by-record-type";
import {
  ADOIT_ID_FIELD_NAME,
  findFormQuestionIdByFieldName,
  getRecordSource,
} from "../../report-plugin";

// Fixed order/colors so the bar and donut chart line up with each other
// (#2599). "User input" is intentionally excluded per the ticket's scoping.
const RECORD_SOURCE_CATEGORIES = ["ADOIT", "Archer", "ADOIT & Archer"] as const;

const getRecordSourceData = (
  items: ReturnType<typeof filterReportingItemsByRecordType>,
  configuration: Configuration | undefined
) => {
  const adoitIdQuestionId = findFormQuestionIdByFieldName(
    configuration,
    ADOIT_ID_FIELD_NAME
  );

  const counts = new Map<string, number>(
    RECORD_SOURCE_CATEGORIES.map((category) => [category, 0])
  );

  for (const item of items) {
    const recordSource = getRecordSource(item, adoitIdQuestionId);
    if (recordSource === "User input") continue;
    counts.set(recordSource, (counts.get(recordSource) ?? 0) + 1);
  }

  const entries = RECORD_SOURCE_CATEGORIES.map((label, index) => ({
    label,
    count: counts.get(label) ?? 0,
    color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
  }));

  return { entries };
};

/**
 * The bar and donut chart pair showing Business Apps per record source
 * across all projects (#2599).
 */
export function addRecordSourceChartsCard(
  view: View<TestAppState, DragAndDropLayout>
): void {
  const recordSourceBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "recordSourceDashboardBarChartComponent",
      labels: ["barChart", "chart", "recordSource"],
      description:
        "Shows Business Apps grouped by record source across all projects.",
      title: "Business Apps per Record Source",
    },
  });

  recordSourceBarChartComponent.mapState(({ dashboard, settings }) => {
    const items = filterReportingItemsByRecordType(
      getAllReportingItems(dashboard),
      APP_RECORD_TYPES.BUSINESS_APPLICATION
    );
    const { entries } = getRecordSourceData(items, settings?.configuration);

    return {
      title: "Business Apps per Record Source",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Business Apps",
      yLabel: "Record Source",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const recordSourceDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "recordSourceDashboardDonutChartComponent",
      labels: ["donutChart", "chart", "recordSource"],
      description:
        "Shows Business Apps grouped by record source across all projects.",
      title: "Business Apps per Record Source",
    },
  });

  recordSourceDonutChartComponent.mapState(({ dashboard, settings }) => {
    const items = filterReportingItemsByRecordType(
      getAllReportingItems(dashboard),
      APP_RECORD_TYPES.BUSINESS_APPLICATION
    );
    const { entries } = getRecordSourceData(items, settings?.configuration);

    return {
      title: withTotalInTitle("Business Apps per Record Source", items.length),
      icon: "DonutLarge",
      ...DONUT_CARD_LAYOUT,
      data: entries.map((entry) => ({
        label: entry.label,
        value: entry.count,
        color: entry.color,
      })),
    };
  });
}
